import { useCallback, useEffect, useState } from 'react';
import { adminApi, type SharedPhoto } from './api.ts';

/**
 * Onglet Photos : les photos partagées par QR code, envoyées par la borne.
 *
 * Une grille de vignettes, une visionneuse plein écran, et l'effacement — une
 * photo effacée ici ne s'ouvre plus depuis son QR code. Le bucket efface de
 * lui-même, chaque nuit, celles qui ont dépassé la durée de conservation.
 */
export function PhotosSection() {
  const [photos, setPhotos] = useState<SharedPhoto[] | null>(null);
  const [retention, setRetention] = useState(7);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { photos: list, retentionDays } = await adminApi.shares();
      setPhotos(list);
      setRetention(retentionDays);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    // Les photos arrivent au fil des séances : la liste suit sans recharger.
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const remove = async (token: string) => {
    setBusy(token);
    try {
      await adminApi.deleteShare(token);
      const next = (photos ?? []).filter((p) => p.token !== token);
      setPhotos(next);
      // La visionneuse passe à la suivante, ou se ferme s'il n'y en a plus.
      setOpen((index) => (index === null || !next.length ? null : Math.min(index, next.length - 1)));
      setConfirm('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  };

  const current = open !== null && photos ? photos[open] : null;

  useEffect(() => {
    if (open === null || !photos) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null);
      else if (event.key === 'ArrowRight') setOpen((i) => (i === null ? i : Math.min(photos.length - 1, i + 1)));
      else if (event.key === 'ArrowLeft') setOpen((i) => (i === null ? i : Math.max(0, i - 1)));
      else return;
      setConfirm('');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, photos]);

  return (
    <section className="card photos">
      <header>
        <h2>
          Photos partagées
          {photos && <span className="photos__count">{photos.length}</span>}
        </h2>
        <button type="button" className="btn btn--ghost btn--small" onClick={() => void load()}>
          Actualiser
        </button>
      </header>

      <p className="card__hint">
        Les photos ouvertes par les visiteurs depuis le QR code de la borne. Elles sont effacées
        automatiquement au bout de {retention} jours ; une photo supprimée ici ne s’ouvre plus depuis
        son QR code.
      </p>

      {error && <p className="admin__error">{error}</p>}
      {!photos && !error && <p className="card__empty">Chargement…</p>}
      {photos && !photos.length && <p className="card__empty">Aucune photo partagée pour le moment.</p>}

      {photos && photos.length > 0 && (
        <ul className="photos__grid">
          {photos.map((photo, index) => (
            <li key={photo.token} className="photos__item">
              <button type="button" className="photos__thumb" onClick={() => setOpen(index)}>
                <img src={`/api/admin/shares/${photo.token}/thumb`} alt="" loading="lazy" />
              </button>
              <div className="photos__meta">
                <span>{when(photo.takenAt)}</span>
                <span className="photos__expiry">{remaining(photo.expiresAt)}</span>
              </div>
              {confirm === photo.token ? (
                <div className="photos__actions">
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => setConfirm('')}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    disabled={busy === photo.token}
                    onClick={() => void remove(photo.token)}
                  >
                    Confirmer
                  </button>
                </div>
              ) : (
                <div className="photos__actions">
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => setConfirm(photo.token)}>
                    Supprimer
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {current && open !== null && photos && (
        <div className="photos__viewer" role="dialog" aria-modal onClick={() => setOpen(null)}>
          <div className="photos__stage" onClick={(e) => e.stopPropagation()}>
            <img src={`/p/${current.token}/photo`} alt="" />
            <div className="photos__bar">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={open === 0}
                onClick={() => {
                  setOpen(open - 1);
                  setConfirm('');
                }}
              >
                ←
              </button>
              <span>
                {when(current.takenAt)} · {open + 1} / {photos.length} · {remaining(current.expiresAt)}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={open === photos.length - 1}
                onClick={() => {
                  setOpen(open + 1);
                  setConfirm('');
                }}
              >
                →
              </button>
              <span className="photos__spacer" />
              {confirm === current.token ? (
                <>
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => setConfirm('')}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    disabled={busy === current.token}
                    onClick={() => void remove(current.token)}
                  >
                    Confirmer la suppression
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn--danger btn--small" onClick={() => setConfirm(current.token)}>
                  Supprimer
                </button>
              )}
              <button type="button" className="btn btn--ghost btn--small" onClick={() => setOpen(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/** « 7 oct. 14:32 » */
function when(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** « encore 5 j », « encore 3 h » avant l'effacement automatique. */
function remaining(expiresAt: string): string {
  const hours = (Date.parse(expiresAt) - Date.now()) / 3_600_000;
  if (hours <= 1) return 'effacée sous 1 h';
  if (hours < 48) return `encore ${Math.floor(hours)} h`;
  return `encore ${Math.floor(hours / 24)} j`;
}
